// src/pages/ConsentInspectorPage.jsx
//
// Consent Inspector — two tools in one page:
//   1. Decode — paste any raw TCF/GPP/us_privacy string and see it decoded.
//   2. Session consistency check — scan the live captured session for every
//      consent-bearing query param (gdpr_consent, gdpr, us_privacy, usp, gpp,
//      gpp_sid) and flag when the SAME param key carries DIFFERENT values
//      across requests in one session — i.e. different bidders/requests are
//      being handed different consent, which is the actual bug this page
//      hunts for.
//
// Talks to the proxy's decode-consent endpoint (proxy-server, parallel PR):
//   POST /proxy/decode-consent  { value }  -> { ok, kind: 'tcf'|'gpp'|'usp'|'unknown', decoded }
// Same PROXY_API_BASE idiom as api/checks.js / api/appPacks.js / api/agentChat.js:
// REACT_APP_PROXY_API_URL in prod, falls back to a bare "/proxy" path relative
// to the page origin for local dev.

import React, { useMemo, useState, useCallback } from 'react';
import { useTraffic } from '../context/TrafficContext';

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

// Query params that carry consent signals, and which "family" they belong to
// (purely cosmetic grouping for the session scan — decoding itself is left to
// the proxy endpoint, which sniffs the kind from the value shape).
const CONSENT_PARAMS = ['gdpr_consent', 'gdpr', 'us_privacy', 'usp', 'gpp', 'gpp_sid'];

async function decodeConsent(value) {
  const res = await fetch(`${PROXY_API_BASE}/proxy/decode-consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  });
  let body = null;
  try { body = await res.json(); } catch { /* ignore */ }
  if (!res.ok) {
    const msg = (body && (body.detail || body.error)) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body;
}

function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  // execCommand fallback for older/insecure contexts
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      ok ? resolve() : reject(new Error('execCommand copy failed'));
    } catch (e) {
      reject(e);
    }
  });
}

function CopyButton({ getText, label = 'Copy JSON' }) {
  const [state, setState] = useState('idle'); // idle | ok | err
  const onClick = useCallback(async () => {
    try {
      await copyToClipboard(getText());
      setState('ok');
    } catch {
      setState('err');
    }
    setTimeout(() => setState('idle'), 1500);
  }, [getText]);
  return (
    <button onClick={onClick} style={btnGhost}>
      {state === 'ok' ? 'Copied' : state === 'err' ? 'Copy failed' : label}
    </button>
  );
}

// Simple recursive key/value tree for the decoded object. Arrays of
// primitives render as chips; arrays of objects (purposes/vendors) render as
// a list of nested <details>; plain objects render as nested <details>.
function ValueTree({ value, depth = 0 }) {
  if (value === null || value === undefined) {
    return <span style={{ color: 'var(--muted)' }}>—</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: 'var(--muted)' }}>[]</span>;
    const allPrimitive = value.every((v) => v === null || typeof v !== 'object');
    if (allPrimitive) {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {value.map((v, i) => (
            <span key={i} style={chip}>{String(v)}</span>
          ))}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {value.map((v, i) => (
          <details key={i} open={depth < 1}>
            <summary style={summaryStyle}>[{i}]</summary>
            <div style={{ paddingLeft: 14, marginTop: 4 }}>
              <ValueTree value={v} depth={depth + 1} />
            </div>
          </details>
        ))}
      </div>
    );
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    if (keys.length === 0) return <span style={{ color: 'var(--muted)' }}>{'{}'}</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {keys.map((k) => {
          const v = value[k];
          const isComplex = v !== null && typeof v === 'object' && Object.keys(v).length > 0;
          if (isComplex) {
            return (
              <details key={k} open={depth < 1}>
                <summary style={summaryStyle}>{k}</summary>
                <div style={{ paddingLeft: 14, marginTop: 4 }}>
                  <ValueTree value={v} depth={depth + 1} />
                </div>
              </details>
            );
          }
          return (
            <div key={k} style={{ display: 'flex', gap: 8, fontSize: 12, padding: '2px 0' }}>
              <span style={{ color: 'var(--muted)', minWidth: 140, fontFamily: 'monospace' }}>{k}</span>
              <span style={{ color: 'var(--ink)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                <ValueTree value={v} depth={depth + 1} />
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  return <span>{String(value)}</span>;
}

const KIND_COLOR = { tcf: '#3b82f6', gpp: '#a855f7', usp: '#f59e0b', unknown: 'var(--muted)' };

function KindBadge({ kind }) {
  const color = KIND_COLOR[kind] || KIND_COLOR.unknown;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 10,
      color, background: color + '22', textTransform: 'uppercase', letterSpacing: 0.4,
    }}>
      {kind || 'unknown'}
    </span>
  );
}

function extractUrl(row) {
  if (row.url) return row.url;
  if (row.host && row.path) return `https://${row.host}${row.path}`;
  return row.path || '';
}

function hostOf(url) {
  try { return new URL(url).host; } catch { return url || ''; }
}

// Pull consent-bearing query params from every row → { paramKey: { value: Set({host, requestType}) } }
function scanSession(rows) {
  const byParam = {}; // paramKey -> Map(value -> { hosts: Set, types: Set, count })
  for (const row of rows) {
    const url = extractUrl(row);
    if (!url) continue;
    let params;
    try {
      params = new URL(url).searchParams;
    } catch {
      continue;
    }
    const host = hostOf(url);
    const type = row.requestType || row.type || 'unknown';
    for (const key of CONSENT_PARAMS) {
      if (!params.has(key)) continue;
      const value = params.get(key);
      if (value === '' || value == null) continue;
      if (!byParam[key]) byParam[key] = new Map();
      const bucket = byParam[key];
      if (!bucket.has(value)) bucket.set(value, { hosts: new Set(), types: new Set(), count: 0 });
      const entry = bucket.get(value);
      entry.hosts.add(host);
      entry.types.add(type);
      entry.count += 1;
    }
  }
  return byParam;
}

export default function ConsentInspectorPage() {
  const traffic = useTraffic();
  const liveTrafficGroups = traffic?.liveTrafficGroups;

  // ── Decode section ──────────────────────────────────────────────────────
  const [rawValue, setRawValue] = useState('');
  const [decodeResult, setDecodeResult] = useState(null); // { ok, kind, decoded }
  const [decodeError, setDecodeError] = useState('');
  const [decoding, setDecoding] = useState(false);

  const handleDecode = useCallback(async () => {
    const value = rawValue.trim();
    if (!value) return;
    setDecoding(true);
    setDecodeError('');
    setDecodeResult(null);
    try {
      const result = await decodeConsent(value);
      setDecodeResult(result);
      if (result && result.ok === false) {
        setDecodeError('Proxy could not decode this value.');
      }
    } catch (e) {
      setDecodeError(e.message || 'Decode request failed — is the proxy reachable?');
    } finally {
      setDecoding(false);
    }
  }, [rawValue]);

  // ── Session consistency section ─────────────────────────────────────────
  const allRows = useMemo(
    () => Object.values(liveTrafficGroups || {}).flat(),
    [liveTrafficGroups]
  );

  const [scanned, setScanned] = useState(false);
  const [scanResult, setScanResult] = useState(null); // byParam map
  const [decodedByValue, setDecodedByValue] = useState({}); // value -> { kind, decoded } | { error }
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');

  const runScan = useCallback(async () => {
    setScanning(true);
    setScanError('');
    const byParam = scanSession(allRows);
    setScanResult(byParam);
    setScanned(true);

    // Decode each DISTINCT value once, sequentially, capped ~10 total.
    const distinctValues = [];
    for (const key of Object.keys(byParam)) {
      for (const value of byParam[key].keys()) {
        if (!distinctValues.includes(value)) distinctValues.push(value);
      }
    }
    const capped = distinctValues.slice(0, 10);
    const results = {};
    for (const value of capped) {
      try {
        const r = await decodeConsent(value);
        results[value] = r;
      } catch (e) {
        results[value] = { error: e.message || 'decode failed' };
      }
    }
    setDecodedByValue(results);
    setScanning(false);
  }, [allRows]);

  const paramKeys = scanResult ? Object.keys(scanResult) : [];

  return (
    <div style={{ padding: 20, display: 'grid', gap: 16, maxWidth: 1100, margin: '0 auto' }}>
      <div>
        <h2 style={{ margin: 0, color: 'var(--ink)' }}>Consent Inspector</h2>
        <p style={{ margin: '4px 0 0 0', color: 'var(--muted)', fontSize: 13 }}>
          Decode raw TCF / GPP / us_privacy consent strings, and scan the current
          session for consent-string mismatches across bidders — the same param
          key (e.g. <code>gdpr_consent</code>) carrying different values on different
          requests usually means a bidder or wrapper isn't propagating consent correctly.
        </p>
      </div>

      {/* ── Section 1: Decode ─────────────────────────────────────────── */}
      <section style={card}>
        <h3 style={sectionTitle}>Decode a consent string</h3>
        <textarea
          value={rawValue}
          onChange={(e) => setRawValue(e.target.value)}
          placeholder="Paste a TCF v2 (CPxxx...), GPP (DBAB...), or us_privacy (1YNN) string here"
          rows={4}
          style={textarea}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
          <button onClick={handleDecode} disabled={decoding || !rawValue.trim()} style={btnPrimary}>
            {decoding ? 'Decoding…' : 'Decode'}
          </button>
          {decodeResult && decodeResult.ok !== false && (
            <CopyButton getText={() => JSON.stringify(decodeResult, null, 2)} />
          )}
        </div>

        {decodeError && (
          <div style={errorNote}>{decodeError}</div>
        )}

        {decodeResult && decodeResult.ok !== false && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <KindBadge kind={decodeResult.kind} />
            </div>
            <div style={resultBox}>
              <ValueTree value={decodeResult.decoded ?? decodeResult} />
            </div>
          </div>
        )}
      </section>

      {/* ── Section 2: Session consistency check ────────────────────────── */}
      <section style={card}>
        <h3 style={sectionTitle}>Session consistency check</h3>
        <p style={{ margin: '0 0 10px 0', color: 'var(--muted)', fontSize: 12 }}>
          Scans every captured request for <code>gdpr_consent</code>, <code>gdpr</code>,{' '}
          <code>us_privacy</code>, <code>usp</code>, <code>gpp</code>, and <code>gpp_sid</code>{' '}
          query params, groups the distinct values seen per key, and flags any key that
          carries more than one distinct value across the session.
        </p>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={runScan} disabled={scanning || allRows.length === 0} style={btnPrimary}>
            {scanning ? 'Scanning…' : 'Scan current session'}
          </button>
          {scanned && (
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {allRows.length} request{allRows.length === 1 ? '' : 's'} in session
            </span>
          )}
        </div>

        {allRows.length === 0 && (
          <div style={hintNote}>No captured traffic yet — connect a device and generate some requests, then scan.</div>
        )}

        {scanError && <div style={errorNote}>{scanError}</div>}

        {scanned && paramKeys.length === 0 && allRows.length > 0 && (
          <div style={hintNote}>No consent-bearing query params found on any captured request.</div>
        )}

        {scanned && paramKeys.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
            {paramKeys.map((key) => {
              const bucket = scanResult[key];
              const distinctValues = [...bucket.keys()];
              const mismatch = distinctValues.length > 1;
              return (
                <div key={key} style={{
                  border: `1px solid ${mismatch ? '#ef4444' : 'var(--line)'}`,
                  borderRadius: 10,
                  padding: 12,
                  background: mismatch ? '#ef444414' : 'var(--surface)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ink)', fontSize: 13 }}>{key}</span>
                    {mismatch ? (
                      <span style={{
                        fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 8,
                        color: '#ef4444', background: '#ef444422', letterSpacing: 0.3,
                      }}>
                        ⚠ MISMATCH — {distinctValues.length} distinct values
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>1 distinct value across session</span>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {distinctValues.map((value) => {
                      const entry = bucket.get(value);
                      const decoded = decodedByValue[value];
                      return (
                        <div key={value} style={{
                          padding: 10, borderRadius: 8, background: 'var(--card, var(--bg))',
                          border: '1px solid var(--line)',
                        }}>
                          <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--ink)', wordBreak: 'break-all', marginBottom: 6 }}>
                            {value}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                            seen {entry.count}x · hosts: {[...entry.hosts].join(', ') || '—'} · types: {[...entry.types].join(', ') || '—'}
                          </div>
                          {decoded ? (
                            decoded.error ? (
                              <div style={{ fontSize: 11, color: '#ef4444' }}>Decode failed: {decoded.error}</div>
                            ) : decoded.ok === false ? (
                              <div style={{ fontSize: 11, color: '#f59e0b' }}>Proxy could not decode this value.</div>
                            ) : (
                              <div>
                                <div style={{ marginBottom: 4 }}><KindBadge kind={decoded.kind} /></div>
                                <div style={{ ...resultBox, padding: 8 }}>
                                  <ValueTree value={decoded.decoded ?? decoded} depth={1} />
                                </div>
                              </div>
                            )
                          ) : (
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>not decoded (over the 10-value cap)</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ── styles ─────────────────────────────────────────────────────────────────
const card = {
  border: '1px solid var(--line)',
  borderRadius: 10,
  padding: 16,
  background: 'var(--surface)',
};
const sectionTitle = { margin: '0 0 10px 0', color: 'var(--ink)', fontSize: 15 };
const textarea = {
  width: '100%', boxSizing: 'border-box', padding: 10, fontSize: 12, fontFamily: 'monospace',
  borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', resize: 'vertical',
};
const btnPrimary = {
  padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none',
  background: '#3b82f6', color: '#fff', cursor: 'pointer',
};
const btnGhost = {
  padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8,
  border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink)', cursor: 'pointer',
};
const errorNote = {
  marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12,
  color: '#ef4444', background: '#ef444414', border: '1px solid #ef444455',
};
const hintNote = {
  marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12,
  color: 'var(--muted)', background: 'var(--bg)', border: '1px dashed var(--line)',
};
const resultBox = {
  padding: 12, borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--line)',
  maxHeight: 420, overflow: 'auto',
};
const summaryStyle = { cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--ink)' };
const chip = {
  fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--bg)',
  border: '1px solid var(--line)', color: 'var(--ink)', fontFamily: 'monospace',
};
